import { styled } from '@compiled/react';
import React, { useEffect, useContext } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';

import Spinner from '@atlaskit/spinner/spinner';

import { PageLoadEnd } from '@confluence/browser-metrics/entry-points/PageLoadEnd.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import {
	RESTRICTED_SPACE_FOR_GUEST_EXPERIENCE,
	SUBMIT_GUEST_REQUEST_TO_UPGRADE_EXPERIENCE,
} from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import {
	ExperienceStart,
	ExperienceSuccess,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker/entry-points/component.ts';
import { GuestRequestToUpgradeDialog } from '@confluence/external-collab-ui/entry-points/GuestRequestToUpgradeLearnMoreModal.ts';
import { LEARN_MORE_LINK } from '@confluence/external-collab-ui/entry-points/constants.ts';
import { GuestLearnMoreLink } from '@confluence/external-collab-ui/entry-points/GuestLearnMoreLink.ts';
import type { WithFlagsProps } from '@confluence/flags/entry-points/withFlags.ts';
import { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { ErrorPageComponent } from '@confluence/not-found/entry-points/ErrorPageComponent.ts';

import { INACCESSIBLE_PAGE_LOAD } from './perf.config.js';
import RestrictedContentIconDark from './RestrictedContentIcon-Dark.svg';
import RestrictedContentIcon from './RestrictedContentIcon-Light.svg';
import { ANALYTICS_SOURCE, useRestrictedSpaceForGuest } from './useRestrictedSpaceForGuest.ts';

export const i18n: {
	errorMessageWithRequestAccess: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	errorMessageWithoutRequestAccess: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	flagSuccessTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	flagSuccessDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	flagErrorTitle: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	flagErrorDescription: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	learnMore: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	learnMoreButton: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	requestAccessButton: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	requestAccessPendingButton: {
		id: string;
		defaultMessage: string;
		description: string;
	};
	title: {
		id: string;
		defaultMessage: string;
		description: string;
	};
} = defineMessages({
	errorMessageWithRequestAccess: {
		id: 'no-permission.restricted.space.guest.requestAccessMessage',
		defaultMessage:
			'Guest users can only access content in one space. Request licensed user access to unlock content in more spaces.',
		description: 'Restricted message when a guest can request access',
	},
	errorMessageWithoutRequestAccess: {
		id: 'no-permission.restricted.space.guest.message',
		defaultMessage:
			'You are a guest on this Confluence site, so you can only access content in one space. {learnMoreLink}',
		description: 'Restricted message when a guest cannot request access',
	},
	flagSuccessTitle: {
		id: 'no-permission.restricted.space.guest.request-access.flag-success-title',
		defaultMessage: 'Licensed user request sent!',
		description:
			'Flag notification title informing the guest that their request to be upgraded to a licensed user was successfully submitted',
	},
	flagSuccessDescription: {
		id: 'no-permission.restricted.space.guest.request-access.flag-success-description',
		defaultMessage: 'If approved, you’ll get a confirmation message from admin',
		description: 'Flag notification description informing guest that request was submitted',
	},
	flagErrorTitle: {
		id: 'no-permission.restricted.space.guest.request-access.flag-error-title',
		defaultMessage: 'Request not sent',
		description: 'Flag notification title informing guest that request encountered an error',
	},
	flagErrorDescription: {
		id: 'no-permission.restricted.space.guest.request-access.flag-error-description',
		defaultMessage: 'Refresh the page to resubmit your request, or try again later.',
		description: 'Flag notification description informing guest that request encountered an error',
	},
	learnMore: {
		id: 'no-permission.restricted.space.learnMore',
		defaultMessage: 'Learn more about guest users',
		description:
			'The text is shown as a hyperlink inside the description on the restricted space error page shown to guest users. When clicked, it opens documentation explaining guest user access restrictions.',
	},
	learnMoreButton: {
		id: 'no-permission.restricted.space.learnMoreButton',
		defaultMessage: 'More about guest users',
		description: 'Restricted message when a guest cannot request access',
	},
	requestAccessButton: {
		id: 'no-permission.restricted.space.request.access.requestAccess',
		defaultMessage: 'Request full access',
		description:
			'After a guest clicks on the button, a request is submitted to upgrade the guest to a full user',
	},
	requestAccessPendingButton: {
		id: 'no-permission.restricted.space.request.access.requestAccessPending',
		defaultMessage: 'Request pending approval',
		description:
			'This text will appear on a disabled button, indicating that the upgrade request is pending',
	},
	title: {
		id: 'no-permission.restricted.space.guest.title',
		defaultMessage: 'You don’t have access to this content',
		description:
			'Page title message shown when guest user lacks permission to view restricted content',
	},
});

const CUSTOM_ATTRIBUTES = {
	inaccessiblePageType: 'RESTRICTED_SPACE_FOR_GUEST',
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const SpinnerContainer = styled.div({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'center',
	alignItems: 'center',
	height: '90vh',
});

type RestrictedSpaceForGuestProps = {};

const RestrictedSpaceForGuestComponent = ({
	flags,
}: RestrictedSpaceForGuestProps & WithFlagsProps) => {
	const { formatMessage } = useIntl();
	const experienceTracker = useContext(ExperienceTrackerContext);

	const {
		fireOnRequestSubmittedAnalytics,
		fireLearnMoreClickedAnalytics,
		fireRequestAccessClickedAnalytics,
		fireViewScreenAnalytics,
		canRequestUpgrade,
		showDialog,
		toggleDialog,
		isRequestAccessPending,
		loading,
	} = useRestrictedSpaceForGuest();

	useEffect(() => {
		if (!loading) {
			fireViewScreenAnalytics(canRequestUpgrade);
		}
	}, [loading, canRequestUpgrade, fireViewScreenAnalytics]);

	if (loading) {
		return (
			<SpinnerContainer>
				<Spinner size="large" interactionName="restricted-space-for-guest" />
			</SpinnerContainer>
		);
	}

	const handleError = (submissionError: Error) => {
		void flags.showErrorFlag({
			title: <FormattedMessage {...i18n.flagErrorTitle} />,
			description: <FormattedMessage {...i18n.flagErrorDescription} />,
		});
		experienceTracker.fail({
			name: SUBMIT_GUEST_REQUEST_TO_UPGRADE_EXPERIENCE,
			error: submissionError,
		});
	};

	const handleOnSubmit = (error?: Error) => {
		fireOnRequestSubmittedAnalytics(!!error);

		if (!error) {
			void flags.showSuccessFlag({
				title: <FormattedMessage {...i18n.flagSuccessTitle} />,
				description: <FormattedMessage {...i18n.flagSuccessDescription} />,
			});
		} else {
			handleError(error);
		}
	};

	const handlePrimaryButtonClick = () => {
		fireRequestAccessClickedAnalytics();
		toggleDialog();
	};

	const handleLearnMoreClick = () => {
		fireLearnMoreClickedAnalytics();
	};

	const errorMessage = canRequestUpgrade ? (
		<FormattedMessage {...i18n.errorMessageWithRequestAccess} />
	) : (
		<FormattedMessage
			{...i18n.errorMessageWithoutRequestAccess}
			values={{
				learnMoreLink: (
					<GuestLearnMoreLink onClick={handleLearnMoreClick}>
						<FormattedMessage {...i18n.learnMore} />
					</GuestLearnMoreLink>
				),
			}}
		/>
	);

	const requestToUpgradeErrorPageProps = canRequestUpgrade && {
		primaryButtonProps: {
			isDisabled: isRequestAccessPending,
			onClick: handlePrimaryButtonClick,
			children: formatMessage(
				isRequestAccessPending ? i18n.requestAccessPendingButton : i18n.requestAccessButton,
			),
		},
		secondaryButtonProps: {
			children: formatMessage(i18n.learnMoreButton),
			href: LEARN_MORE_LINK,
			target: '_blank',
			onClick: handleLearnMoreClick,
		},
	};

	return (
		<>
			<ErrorPageComponent
				errorImage={RestrictedContentIcon}
				errorImageDark={RestrictedContentIconDark}
				errorTitle={<FormattedMessage {...i18n.title} />}
				errorMessage={errorMessage}
				{...requestToUpgradeErrorPageProps}
			>
				{showDialog && (
					<GuestRequestToUpgradeDialog
						analytics={{
							touchpointId: ANALYTICS_SOURCE,
						}}
						onSubmit={handleOnSubmit}
						requestPending={isRequestAccessPending}
						closeModal={toggleDialog}
					/>
				)}
			</ErrorPageComponent>
			<ExperienceSuccess name={RESTRICTED_SPACE_FOR_GUEST_EXPERIENCE} />
			<PageLoadEnd metric={INACCESSIBLE_PAGE_LOAD} customData={CUSTOM_ATTRIBUTES} />
		</>
	);
};

export const RestrictedSpaceForGuest = (props: RestrictedSpaceForGuestProps): React.JSX.Element => (
	<ErrorBoundary attribution={Attribution.EXTERNAL_COLLAB}>
		<ExperienceStart name={RESTRICTED_SPACE_FOR_GUEST_EXPERIENCE} />
		<Subscribe to={[FlagsStateContainer]}>
			{(flags: FlagsStateContainer) => (
				<RestrictedSpaceForGuestComponent {...props} flags={flags} />
			)}
		</Subscribe>
	</ErrorBoundary>
);
